import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, AlertDescription, Badge, Button, Input, Label, Textarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { AlertCircle, ChevronDown, ChevronRight, Edit2, FileText, Save, X } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCLINHistory } from '../../../hooks/useCLINHistory';
import { usePortal } from '../../../hooks/usePortal';
import { useUpdateCLIN } from '../../../hooks/useUpdateCLIN';
import { proposalProjectApiConnect } from '../../../lib/api/proposalProjectApi.connect';
import { EntityAuditTrail } from '../../common/EntityAuditTrail';
import { DocumentViewerDrawer } from '../DocumentViewer/DocumentViewerDrawer';
import { AmendmentTimeline } from '../v2/amendment/AmendmentTimeline';
const optionTypeLabels = {
    base: 'Base',
    option_period: 'Option Period',
    optional_task: 'Optional Task'
};
const formatPrice = (value)=>{
    if (!value) return 'Not specified';
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `$${num.toLocaleString()}`;
};
const parsePageFromBlockId = (blockId)=>{
    if (!blockId) return undefined;
    const m = blockId.match(/\/page\/(\d+)\//i);
    if (!m) return undefined;
    const idx = Number.parseInt(m[1], 10);
    if (Number.isNaN(idx)) return undefined;
    return idx + 1;
};
const normalizeBlockId = (blockId)=>{
    if (!blockId) return '';
    return blockId.replace(/^coalesced-/, '');
};
const contentContainsClin = (content, clinNumber)=>{
    if (!content || !clinNumber) return false;
    const escaped = clinNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const rx = new RegExp(`\\b${escaped}\\b`, 'i');
    return rx.test(content);
};
export const CLINDetailDrawer = /*#__PURE__*/ memo(function CLINDetailDrawer({ clin, projectId, isOpen, onClose, referenceContent }) {
    const portalContainer = usePortal('clin-detail-drawer-portal');
    const [isEditMode, setIsEditMode] = useState(false);
    const [formData, setFormData] = useState({
        title: clin.title,
        description: clin.description || '',
        pricing: clin.pricing ? {
            ...clin.pricing
        } : {}
    });
    const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
    const [viewerTarget, setViewerTarget] = useState(null);
    const [expandedRefs, setExpandedRefs] = useState(new Set());
    const [isResolvingSource, setIsResolvingSource] = useState(false);
    const updateCLIN = useUpdateCLIN(projectId);
    // PR-C.5: pull the amendment-version chain for this CLIN from the
    // project's full CLIN list. The hook reuses the existing useCLINs
    // cache and groups by logicalKey (set by the merge step). Returns
    // an empty history when the CLIN has only one version.
    const history = useCLINHistory(projectId, clin);
    const timelineEntries = useMemo(()=>history.versions.map((row)=>({
                key: `${row.clinNumber}-${row.amendmentNumber ?? 'base'}-${row.amendmentSequence ?? 0}`,
                amendmentNumber: row.amendmentNumber,
                amendmentSequence: row.amendmentSequence,
                isCurrent: row.isCurrentVersion,
                sourceFilename: row.sourceFilename ?? row.source?.filename,
                primary: buildClinPrimary(row),
                secondary: buildClinSecondary(row)
            })), [
        history.versions
    ]);
    // Build source document reference from CLIN source data
    const sourceDocument = useMemo(()=>{
        const src = clin.source;
        if (!src?.documentId) return null;
        return {
            documentId: src.documentId,
            filename: src.filename || 'Source Document',
            documentType: src.documentType
        };
    }, [
        clin.source
    ]);
    const handleSave = async ()=>{
        try {
            // clinId is the gorm primary key — clin_number is not unique per
            // project (amendment versions can collide), so the patch must
            // target the primary key. Fall back to clinNumber only if older
            // cached rows didn't carry clinId; the backend's clin_number
            // lookup path is gone after this fix.
            const targetId = clin.clinId ?? clin.clinNumber;
            await updateCLIN.mutateAsync({
                clinId: targetId,
                title: formData.title,
                description: formData.description,
                pricing: formData.pricing
            });
            setIsEditMode(false);
        } catch (error) {
            console.error('Failed to update CLIN:', error);
        }
    };
    const handleCancel = ()=>{
        setFormData({
            title: clin.title,
            description: clin.description || '',
            pricing: clin.pricing ? {
                ...clin.pricing
            } : {}
        });
        setIsEditMode(false);
    };
    const handleClose = ()=>{
        if (isEditMode) {
            handleCancel();
        }
        onClose();
    };
    const resolveClinSourceTarget = async ()=>{
        const source = clin.source;
        const fallbackBlockId = source?.sourceBlockIds?.[0] || source?.blockId;
        const fallbackPage = source?.pageNumber || parsePageFromBlockId(fallbackBlockId);
        if (!sourceDocument?.documentId) {
            return {
                highlightBlockId: fallbackBlockId,
                initialPage: fallbackPage
            };
        }
        const normalizedFallbackBlock = normalizeBlockId(fallbackBlockId);
        const hasMultiSourceBlocks = (source?.sourceBlockIds?.length ?? 0) > 1;
        const candidatePages = Array.from(new Set((source?.sourceBlockIds ?? []).map((id)=>parsePageFromBlockId(id)).filter((p)=>!!p && p > 0)));
        if (fallbackPage && !candidatePages.includes(fallbackPage)) {
            candidatePages.unshift(fallbackPage);
        }
        if (candidatePages.length === 0) {
            return {
                highlightBlockId: fallbackBlockId,
                initialPage: fallbackPage
            };
        }
        for (const page of candidatePages){
            try {
                const visualization = await proposalProjectApiConnect.getPageVisualization(projectId, sourceDocument.documentId, page);
                if (!visualization?.blocks?.length) continue;
                const clinTextMatch = visualization.blocks.find((b)=>contentContainsClin(b.content ?? '', clin.clinNumber));
                if (clinTextMatch) {
                    return {
                        highlightBlockId: clinTextMatch.id,
                        initialPage: page
                    };
                }
                // Exact block-id fallback is useful only when we don't have multi-block provenance
                // (coalesced table sources otherwise pin too many CLINs to the same anchor block).
                if (!hasMultiSourceBlocks) {
                    const exactIdMatch = visualization.blocks.find((b)=>normalizeBlockId(b.id) === normalizedFallbackBlock && normalizedFallbackBlock);
                    if (exactIdMatch) {
                        return {
                            highlightBlockId: exactIdMatch.id,
                            initialPage: page
                        };
                    }
                }
            } catch  {
            // best effort resolver; continue with remaining candidate pages
            }
        }
        return {
            highlightBlockId: fallbackBlockId,
            initialPage: fallbackPage
        };
    };
    if (!isOpen || !portalContainer) {
        return null;
    }
    return /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity",
                onClick: handleClose
            }),
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.clin.drawer,
                className: "fixed inset-y-0 right-0 w-full max-w-3xl bg-white shadow-xl z-50 flex flex-col",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "px-6 py-4 border-b border-gray-200 flex items-start justify-between",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsxs("h2", {
                                        className: "text-xl font-semibold",
                                        children: [
                                            "CLIN ",
                                            clin.clinNumber
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "text-sm text-gray-500 mt-1",
                                        children: [
                                            "Contract Line Item Number Details",
                                            clin.parentClin && /*#__PURE__*/ _jsxs("span", {
                                                className: "ml-2 text-gray-400",
                                                children: [
                                                    "(Sub of ",
                                                    clin.parentClin,
                                                    ")"
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    isEditMode ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsxs(Button, {
                                                size: "sm",
                                                "data-testid": Testids.clin.saveButton,
                                                onClick: handleSave,
                                                disabled: updateCLIN.isPending,
                                                children: [
                                                    /*#__PURE__*/ _jsx(Save, {
                                                        className: "w-4 h-4 mr-2"
                                                    }),
                                                    "Save"
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs(Button, {
                                                size: "sm",
                                                variant: "outline",
                                                onClick: handleCancel,
                                                children: [
                                                    /*#__PURE__*/ _jsx(X, {
                                                        className: "w-4 h-4 mr-2"
                                                    }),
                                                    "Cancel"
                                                ]
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsxs(Button, {
                                        size: "sm",
                                        variant: "outline",
                                        "data-testid": Testids.clin.editButton,
                                        onClick: ()=>setIsEditMode(true),
                                        children: [
                                            /*#__PURE__*/ _jsx(Edit2, {
                                                className: "w-4 h-4 mr-2"
                                            }),
                                            "Edit"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        size: "sm",
                                        variant: "ghost",
                                        onClick: handleClose,
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "w-4 h-4"
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1 overflow-y-auto px-6 py-6",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-6",
                            children: [
                                updateCLIN.isError && /*#__PURE__*/ _jsxs(Alert, {
                                    variant: "destructive",
                                    children: [
                                        /*#__PURE__*/ _jsx(AlertCircle, {
                                            className: "h-4 w-4"
                                        }),
                                        /*#__PURE__*/ _jsxs(AlertDescription, {
                                            children: [
                                                "Failed to update CLIN:",
                                                ' ',
                                                updateCLIN.error instanceof Error ? updateCLIN.error.message : 'Unknown error'
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-2 flex-wrap",
                                    children: [
                                        clin.status && /*#__PURE__*/ _jsx(Badge, {
                                            variant: "secondary",
                                            className: clin.status === 'confirmed' ? 'bg-green-100 text-green-800' : clin.status === 'disputed' ? 'bg-red-100 text-red-800' : clin.status === 'reviewed' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800',
                                            children: clin.status
                                        }),
                                        clin.isOptionalTask && /*#__PURE__*/ _jsxs(Badge, {
                                            variant: "secondary",
                                            className: "bg-orange-100 text-orange-800 border-orange-200",
                                            children: [
                                                "Optional Task ",
                                                clin.optionalTaskNumber || ''
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Basic Information"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    htmlFor: "title",
                                                    children: "Title"
                                                }),
                                                isEditMode ? /*#__PURE__*/ _jsx(Input, {
                                                    id: "title",
                                                    value: formData.title,
                                                    onChange: (e)=>setFormData({
                                                            ...formData,
                                                            title: e.target.value
                                                        }),
                                                    className: "mt-1"
                                                }) : /*#__PURE__*/ _jsx("p", {
                                                    className: "mt-1 text-sm text-gray-900",
                                                    children: clin.title || 'Untitled'
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    htmlFor: "description",
                                                    children: "Description"
                                                }),
                                                isEditMode ? /*#__PURE__*/ _jsx(Textarea, {
                                                    id: "description",
                                                    value: formData.description,
                                                    onChange: (e)=>setFormData({
                                                            ...formData,
                                                            description: e.target.value
                                                        }),
                                                    rows: 4,
                                                    className: "mt-1"
                                                }) : /*#__PURE__*/ _jsx("p", {
                                                    className: "mt-1 text-sm text-gray-700 whitespace-pre-wrap",
                                                    children: clin.description || 'No description available'
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-4",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Option Type"
                                                        }),
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: "mt-1 text-sm",
                                                            children: clin.optionType ? /*#__PURE__*/ _jsx(Badge, {
                                                                variant: "outline",
                                                                children: optionTypeLabels[clin.optionType] || clin.optionType
                                                            }) : /*#__PURE__*/ _jsx("span", {
                                                                className: "text-gray-400",
                                                                children: "Not specified"
                                                            })
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Contract Type"
                                                        }),
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: "mt-1 text-sm",
                                                            children: clin.contractType ? /*#__PURE__*/ _jsx(Badge, {
                                                                variant: "outline",
                                                                children: clin.contractType
                                                            }) : /*#__PURE__*/ _jsx("span", {
                                                                className: "text-gray-400",
                                                                children: "Not specified"
                                                            })
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        (clin.periodName || clin.correspondingBaseClin || clin.isOptionalTask) && /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-4",
                                            children: [
                                                clin.periodName && /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Period"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-sm text-gray-900",
                                                            children: clin.periodName
                                                        })
                                                    ]
                                                }),
                                                clin.correspondingBaseClin && /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Corresponding Base CLIN"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-sm font-mono text-gray-900",
                                                            children: clin.correspondingBaseClin
                                                        })
                                                    ]
                                                }),
                                                clin.isOptionalTask && clin.optionalTaskTitle && /*#__PURE__*/ _jsxs("div", {
                                                    className: "col-span-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Optional Task"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("p", {
                                                            className: "mt-1 text-sm text-gray-900",
                                                            children: [
                                                                "Task ",
                                                                clin.optionalTaskNumber,
                                                                ": ",
                                                                clin.optionalTaskTitle
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Pricing"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-4",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            htmlFor: "unit-price",
                                                            children: "Unit Price"
                                                        }),
                                                        isEditMode ? /*#__PURE__*/ _jsx(Input, {
                                                            id: "unit-price",
                                                            "data-testid": Testids.clin.unitPriceInput,
                                                            value: formData.pricing?.unitPrice ?? '',
                                                            onChange: (e)=>setFormData({
                                                                    ...formData,
                                                                    pricing: {
                                                                        ...formData.pricing,
                                                                        unitPrice: e.target.value
                                                                    }
                                                                }),
                                                            placeholder: "e.g. $187.42",
                                                            className: "mt-1"
                                                        }) : /*#__PURE__*/ _jsx("p", {
                                                            id: "unit-price",
                                                            "data-testid": Testids.clin.unitPriceDisplay,
                                                            className: "mt-1 text-sm",
                                                            children: formatPrice(clin.pricing?.unitPrice)
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Quantity"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-sm",
                                                            children: clin.pricing?.quantity || 'Not specified'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            htmlFor: "extended-price",
                                                            children: "Extended Price"
                                                        }),
                                                        isEditMode ? /*#__PURE__*/ _jsx(Input, {
                                                            id: "extended-price",
                                                            "data-testid": Testids.clin.extendedPriceInput,
                                                            value: formData.pricing?.extendedPrice ?? '',
                                                            onChange: (e)=>setFormData({
                                                                    ...formData,
                                                                    pricing: {
                                                                        ...formData.pricing,
                                                                        extendedPrice: e.target.value
                                                                    }
                                                                }),
                                                            placeholder: "e.g. $1,000,000.00",
                                                            className: "mt-1"
                                                        }) : /*#__PURE__*/ _jsx("p", {
                                                            id: "extended-price",
                                                            "data-testid": Testids.clin.extendedPriceDisplay,
                                                            className: "mt-1 text-sm",
                                                            children: formatPrice(clin.pricing?.extendedPrice)
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Ceiling (NTE)"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-sm",
                                                            children: formatPrice(clin.pricing?.ceiling)
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                clin.needsTeamPricing && clin.extractedTeamTiers && clin.extractedTeamTiers.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Team Composition"
                                        }),
                                        (()=>{
                                            // Deduplicate tiers by label
                                            const seen = new Set();
                                            const uniqueTiers = (clin.extractedTeamTiers ?? []).filter((t)=>{
                                                if (seen.has(t.label)) return false;
                                                seen.add(t.label);
                                                return true;
                                            });
                                            return uniqueTiers.map((tier)=>/*#__PURE__*/ _jsxs("div", {
                                                    className: "border border-indigo-100 rounded-lg p-3 bg-indigo-50/30",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center gap-2 mb-2",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Badge, {
                                                                    variant: "outline",
                                                                    className: "bg-indigo-100 text-indigo-800 border-indigo-200",
                                                                    children: tier.label
                                                                }),
                                                                tier.definition && /*#__PURE__*/ _jsxs("span", {
                                                                    className: "text-xs text-gray-500",
                                                                    children: [
                                                                        tier.definition.totalFtes,
                                                                        " FTE",
                                                                        tier.definition.totalFtes !== 1 ? 's' : ''
                                                                    ]
                                                                })
                                                            ]
                                                        }),
                                                        tier.definition && tier.definition.roles.length > 0 ? /*#__PURE__*/ _jsxs("table", {
                                                            className: "w-full text-sm",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("thead", {
                                                                    children: /*#__PURE__*/ _jsxs("tr", {
                                                                        className: "text-xs text-gray-500 border-b border-indigo-100",
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx("th", {
                                                                                className: "text-left py-1 font-medium",
                                                                                children: "Role"
                                                                            }),
                                                                            /*#__PURE__*/ _jsx("th", {
                                                                                className: "text-right py-1 font-medium w-16",
                                                                                children: "Count"
                                                                            }),
                                                                            /*#__PURE__*/ _jsx("th", {
                                                                                className: "text-right py-1 font-medium w-16",
                                                                                children: "FTEs"
                                                                            })
                                                                        ]
                                                                    })
                                                                }),
                                                                /*#__PURE__*/ _jsx("tbody", {
                                                                    children: tier.definition.roles.map((role, idx)=>/*#__PURE__*/ _jsxs("tr", {
                                                                            className: "border-b border-indigo-50 last:border-0",
                                                                            children: [
                                                                                /*#__PURE__*/ _jsx("td", {
                                                                                    className: "py-1 text-gray-700",
                                                                                    children: role.title
                                                                                }),
                                                                                /*#__PURE__*/ _jsx("td", {
                                                                                    className: "py-1 text-right text-gray-600",
                                                                                    children: role.count
                                                                                }),
                                                                                /*#__PURE__*/ _jsx("td", {
                                                                                    className: "py-1 text-right text-gray-600",
                                                                                    children: role.ftes
                                                                                })
                                                                            ]
                                                                        }, idx))
                                                                })
                                                            ]
                                                        }) : /*#__PURE__*/ _jsx("p", {
                                                            className: "text-xs text-gray-400",
                                                            children: "Definition pending"
                                                        }),
                                                        tier.definition?.sourceLocation && /*#__PURE__*/ _jsxs("p", {
                                                            className: "text-xs text-gray-400 mt-1",
                                                            children: [
                                                                "Source: ",
                                                                tier.definition.sourceLocation
                                                            ]
                                                        })
                                                    ]
                                                }, tier.label));
                                        })()
                                    ]
                                }),
                                clin.exerciseInfo && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Exercise & Performance Period"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-4",
                                            children: [
                                                clin.exerciseInfo.farClause && /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "FAR Clause"
                                                        }),
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: "mt-1 text-sm",
                                                            children: /*#__PURE__*/ _jsx(Badge, {
                                                                variant: "outline",
                                                                className: "font-mono",
                                                                children: clin.exerciseInfo.farClause
                                                            })
                                                        })
                                                    ]
                                                }),
                                                clin.exerciseInfo.exerciseFrequency && /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Exercise Frequency"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-sm capitalize",
                                                            children: clin.exerciseInfo.exerciseFrequency === 'once' ? 'One time' : 'Multiple times'
                                                        })
                                                    ]
                                                }),
                                                (clin.exerciseInfo.popMonths ?? 0) > 0 && /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Period of Performance"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-sm",
                                                            children: clin.exerciseInfo.popDescription || `${clin.exerciseInfo.popMonths} months`
                                                        })
                                                    ]
                                                }),
                                                clin.exerciseInfo.allowsCrossPeriod && /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Cross-Period"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-sm text-amber-700",
                                                            children: "May cross performance periods"
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        clin.exerciseInfo.popIsConditional && clin.exerciseInfo.conditionalPops && clin.exerciseInfo.conditionalPops.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    children: "Conditional PoP"
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "mt-1 space-y-1",
                                                    children: clin.exerciseInfo.conditionalPops.map((cp, idx)=>/*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center gap-2 text-sm text-gray-700",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Badge, {
                                                                    variant: "outline",
                                                                    className: "text-xs capitalize",
                                                                    children: cp.duringPeriod
                                                                }),
                                                                /*#__PURE__*/ _jsxs("span", {
                                                                    children: [
                                                                        "up to ",
                                                                        cp.popMonths,
                                                                        " months"
                                                                    ]
                                                                })
                                                            ]
                                                        }, idx))
                                                })
                                            ]
                                        }),
                                        clin.exerciseInfo.popUpperBound && /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    children: "Upper Bound"
                                                }),
                                                /*#__PURE__*/ _jsxs("p", {
                                                    className: "mt-1 text-sm text-gray-700",
                                                    children: [
                                                        "Not to exceed ",
                                                        clin.exerciseInfo.popUpperBound
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                (clin.pwsReferences && clin.pwsReferences.length > 0 || clin.scopedPwsRefs && clin.scopedPwsRefs.length > 0 || clin.attachmentReferences && clin.attachmentReferences.length > 0) && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "References"
                                        }),
                                        clin.scopedPwsRefs && clin.scopedPwsRefs.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                            className: "space-y-2",
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    children: "PWS References (Scoped)"
                                                }),
                                                clin.scopedPwsRefs.map((scopedRef)=>{
                                                    const sectionContent = referenceContent?.pwsSections?.[scopedRef.section];
                                                    const hasDocLink = !!sectionContent?.documentId;
                                                    return /*#__PURE__*/ _jsxs("div", {
                                                        className: "border border-purple-100 rounded-lg p-3 bg-purple-50/30",
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("div", {
                                                                className: "flex items-center gap-2 flex-wrap",
                                                                children: [
                                                                    /*#__PURE__*/ _jsxs(Badge, {
                                                                        variant: "outline",
                                                                        className: `text-xs bg-purple-100 text-purple-800 border-purple-200 ${hasDocLink ? 'cursor-pointer hover:bg-purple-200 transition-colors' : ''}`,
                                                                        onClick: hasDocLink ? ()=>{
                                                                            setViewerTarget({
                                                                                document: {
                                                                                    documentId: sectionContent?.documentId ?? '',
                                                                                    filename: sectionContent?.filename || 'PWS Document'
                                                                                },
                                                                                highlightBlockId: sectionContent?.blockId,
                                                                                highlightBlockIds: sectionContent?.blockIds,
                                                                                initialPage: sectionContent?.pageNumber
                                                                            });
                                                                            setIsDocViewerOpen(true);
                                                                        } : undefined,
                                                                        children: [
                                                                            "PWS ",
                                                                            scopedRef.section,
                                                                            hasDocLink && /*#__PURE__*/ _jsx(FileText, {
                                                                                className: "w-3 h-3 ml-1 inline"
                                                                            })
                                                                        ]
                                                                    }),
                                                                    scopedRef.title && /*#__PURE__*/ _jsx("span", {
                                                                        className: "text-xs text-gray-600 font-medium",
                                                                        children: scopedRef.title
                                                                    })
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs("div", {
                                                                className: "mt-1.5 text-xs text-gray-500",
                                                                children: [
                                                                    scopedRef.includesAll && /*#__PURE__*/ _jsxs("span", {
                                                                        className: "inline-flex items-center gap-1 mr-2",
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx("span", {
                                                                                className: "w-1.5 h-1.5 rounded-full bg-green-400"
                                                                            }),
                                                                            "All subparagraphs"
                                                                        ]
                                                                    }),
                                                                    scopedRef.onlyChildren && scopedRef.onlyChildren.length > 0 && /*#__PURE__*/ _jsxs("span", {
                                                                        className: "inline-flex items-center gap-1 mr-2",
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx("span", {
                                                                                className: "w-1.5 h-1.5 rounded-full bg-blue-400"
                                                                            }),
                                                                            "Only: ",
                                                                            scopedRef.onlyChildren.join(', ')
                                                                        ]
                                                                    }),
                                                                    scopedRef.excludes && scopedRef.excludes.length > 0 && /*#__PURE__*/ _jsxs("span", {
                                                                        className: "inline-flex items-center gap-1 mr-2",
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx("span", {
                                                                                className: "w-1.5 h-1.5 rounded-full bg-red-400"
                                                                            }),
                                                                            "Except: ",
                                                                            scopedRef.excludes.join(', ')
                                                                        ]
                                                                    }),
                                                                    !scopedRef.includesAll && !scopedRef.onlyChildren?.length && !scopedRef.excludes?.length && /*#__PURE__*/ _jsx("span", {
                                                                        className: "text-gray-400",
                                                                        children: "Section only"
                                                                    })
                                                                ]
                                                            }),
                                                            scopedRef.expandedSections && scopedRef.expandedSections.length > 1 && /*#__PURE__*/ _jsx("div", {
                                                                className: "mt-2 flex flex-wrap gap-1",
                                                                children: scopedRef.expandedSections.map((sec)=>{
                                                                    const subContent = referenceContent?.pwsSections?.[sec];
                                                                    const subHasDocLink = !!subContent?.documentId;
                                                                    return /*#__PURE__*/ _jsxs(Badge, {
                                                                        variant: "secondary",
                                                                        className: `text-[10px] px-1.5 py-0 ${subHasDocLink ? 'cursor-pointer hover:bg-purple-200 transition-colors' : ''}`,
                                                                        onClick: subHasDocLink ? (e)=>{
                                                                            e.stopPropagation();
                                                                            setViewerTarget({
                                                                                document: {
                                                                                    documentId: subContent?.documentId ?? '',
                                                                                    filename: subContent?.filename || 'PWS Document'
                                                                                },
                                                                                highlightBlockId: subContent?.blockId,
                                                                                highlightBlockIds: subContent?.blockIds,
                                                                                initialPage: subContent?.pageNumber
                                                                            });
                                                                            setIsDocViewerOpen(true);
                                                                        } : undefined,
                                                                        children: [
                                                                            sec,
                                                                            subHasDocLink && /*#__PURE__*/ _jsx(FileText, {
                                                                                className: "w-2.5 h-2.5 ml-0.5 inline"
                                                                            })
                                                                        ]
                                                                    }, sec);
                                                                })
                                                            })
                                                        ]
                                                    }, scopedRef.section);
                                                })
                                            ]
                                        }),
                                        (!clin.scopedPwsRefs || clin.scopedPwsRefs.length === 0) && clin.pwsReferences && clin.pwsReferences.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                            className: "space-y-2",
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    children: "PWS References"
                                                }),
                                                clin.pwsReferences.map((ref)=>{
                                                    const section = referenceContent?.pwsSections?.[ref];
                                                    const isExpanded = expandedRefs.has(`pws-${ref}`);
                                                    return /*#__PURE__*/ _jsxs("div", {
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("button", {
                                                                type: "button",
                                                                className: "flex items-center gap-2 w-full text-left",
                                                                onClick: ()=>{
                                                                    if (!section) return;
                                                                    setExpandedRefs((prev)=>{
                                                                        const next = new Set(prev);
                                                                        if (next.has(`pws-${ref}`)) next.delete(`pws-${ref}`);
                                                                        else next.add(`pws-${ref}`);
                                                                        return next;
                                                                    });
                                                                },
                                                                children: [
                                                                    section ? isExpanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                                                                        className: "w-3.5 h-3.5 text-gray-400 shrink-0"
                                                                    }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                                                        className: "w-3.5 h-3.5 text-gray-400 shrink-0"
                                                                    }) : null,
                                                                    /*#__PURE__*/ _jsxs(Badge, {
                                                                        variant: "outline",
                                                                        className: `text-xs ${section?.documentId ? 'cursor-pointer hover:bg-purple-100 text-purple-800 border-purple-200 transition-colors' : ''}`,
                                                                        onClick: section?.documentId ? (e)=>{
                                                                            e.stopPropagation();
                                                                            setViewerTarget({
                                                                                document: {
                                                                                    documentId: section.documentId ?? '',
                                                                                    filename: section.filename || 'PWS Document'
                                                                                },
                                                                                highlightBlockId: section.blockId,
                                                                                highlightBlockIds: section.blockIds,
                                                                                initialPage: section.pageNumber
                                                                            });
                                                                            setIsDocViewerOpen(true);
                                                                        } : undefined,
                                                                        children: [
                                                                            "PWS ",
                                                                            ref,
                                                                            section?.documentId && /*#__PURE__*/ _jsx(FileText, {
                                                                                className: "w-3 h-3 ml-1 inline"
                                                                            })
                                                                        ]
                                                                    }),
                                                                    section?.title && /*#__PURE__*/ _jsx("span", {
                                                                        className: "text-xs text-gray-500 truncate",
                                                                        children: section.title
                                                                    })
                                                                ]
                                                            }),
                                                            isExpanded && section && /*#__PURE__*/ _jsx("div", {
                                                                className: "mt-1 ml-5 p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto",
                                                                children: section.text || 'No content available'
                                                            })
                                                        ]
                                                    }, ref);
                                                })
                                            ]
                                        }),
                                        clin.attachmentReferences && clin.attachmentReferences.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                            className: "space-y-2",
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    children: "Attachment References"
                                                }),
                                                clin.attachmentReferences.map((ref)=>{
                                                    const attachment = referenceContent?.attachments?.[ref];
                                                    const isExpanded = expandedRefs.has(`att-${ref}`);
                                                    return /*#__PURE__*/ _jsxs("div", {
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("button", {
                                                                type: "button",
                                                                className: "flex items-center gap-2 w-full text-left",
                                                                onClick: ()=>{
                                                                    if (!attachment) return;
                                                                    setExpandedRefs((prev)=>{
                                                                        const next = new Set(prev);
                                                                        if (next.has(`att-${ref}`)) next.delete(`att-${ref}`);
                                                                        else next.add(`att-${ref}`);
                                                                        return next;
                                                                    });
                                                                },
                                                                children: [
                                                                    attachment ? isExpanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                                                                        className: "w-3.5 h-3.5 text-gray-400 shrink-0"
                                                                    }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                                                        className: "w-3.5 h-3.5 text-gray-400 shrink-0"
                                                                    }) : null,
                                                                    /*#__PURE__*/ _jsx(Badge, {
                                                                        variant: "outline",
                                                                        className: "text-xs",
                                                                        children: ref
                                                                    })
                                                                ]
                                                            }),
                                                            isExpanded && attachment && /*#__PURE__*/ _jsxs("div", {
                                                                className: "mt-1 ml-5 p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700 space-y-1",
                                                                children: [
                                                                    /*#__PURE__*/ _jsxs("div", {
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx("span", {
                                                                                className: "font-medium",
                                                                                children: "File: "
                                                                            }),
                                                                            attachment.filename
                                                                        ]
                                                                    }),
                                                                    /*#__PURE__*/ _jsxs("div", {
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx("span", {
                                                                                className: "font-medium",
                                                                                children: "Type: "
                                                                            }),
                                                                            attachment.documentType
                                                                        ]
                                                                    }),
                                                                    attachment.summary && /*#__PURE__*/ _jsx("div", {
                                                                        className: "whitespace-pre-wrap",
                                                                        children: attachment.summary
                                                                    })
                                                                ]
                                                            })
                                                        ]
                                                    }, ref);
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                clin.deliverables && clin.deliverables.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsxs("h3", {
                                            className: "text-lg font-semibold",
                                            children: [
                                                "Deliverables (",
                                                clin.deliverables.length,
                                                ")"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "space-y-3",
                                            children: clin.deliverables.map((deliverable, idx)=>/*#__PURE__*/ _jsxs("div", {
                                                    className: "border border-gray-200 rounded-lg p-4 bg-gray-50",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: "font-medium text-sm",
                                                            children: deliverable.title || 'Untitled deliverable'
                                                        }),
                                                        deliverable.description && /*#__PURE__*/ _jsx("p", {
                                                            className: "text-sm text-gray-600 mt-1",
                                                            children: deliverable.description
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex gap-4 mt-2 text-xs text-gray-500",
                                                            children: [
                                                                deliverable.cdrlReference && /*#__PURE__*/ _jsxs("span", {
                                                                    children: [
                                                                        "CDRL: ",
                                                                        deliverable.cdrlReference
                                                                    ]
                                                                }),
                                                                deliverable.dueDate && /*#__PURE__*/ _jsxs("span", {
                                                                    children: [
                                                                        "Due: ",
                                                                        deliverable.dueDate
                                                                    ]
                                                                }),
                                                                deliverable.cadence && /*#__PURE__*/ _jsxs("span", {
                                                                    children: [
                                                                        "Cadence: ",
                                                                        deliverable.cadence
                                                                    ]
                                                                }),
                                                                deliverable.deliverableType && /*#__PURE__*/ _jsxs("span", {
                                                                    children: [
                                                                        "Type: ",
                                                                        deliverable.deliverableType
                                                                    ]
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                }, deliverable.deliverableId || idx))
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Source Information"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-4 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Document"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: clin.source?.filename || 'Not available'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Section"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: clin.source?.sectionNumber || 'Not available'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Page"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: clin.source?.pageNumber != null ? clin.source.pageNumber : 'Not available'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Confidence"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: clin.confidence != null ? `${(clin.confidence * 100).toFixed(0)}%` : 'N/A'
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        clin.source?.documentId && sourceDocument && /*#__PURE__*/ _jsxs(Button, {
                                            variant: "outline",
                                            className: "w-full mt-4",
                                            disabled: isResolvingSource,
                                            onClick: async ()=>{
                                                setIsResolvingSource(true);
                                                try {
                                                    const target = await resolveClinSourceTarget();
                                                    setViewerTarget({
                                                        document: sourceDocument,
                                                        highlightBlockId: target.highlightBlockId,
                                                        initialPage: target.initialPage
                                                    });
                                                    setIsDocViewerOpen(true);
                                                } finally{
                                                    setIsResolvingSource(false);
                                                }
                                            },
                                            children: [
                                                /*#__PURE__*/ _jsx(FileText, {
                                                    className: "w-4 h-4 mr-2"
                                                }),
                                                isResolvingSource ? 'Locating Exact Source...' : 'View Source in Document'
                                            ]
                                        })
                                    ]
                                }),
                                history.hasHistory && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-3 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Amendment Versions"
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "-mt-2 text-xs text-slate-500",
                                            children: [
                                                "CLIN ",
                                                clin.clinNumber,
                                                " appears in ",
                                                history.versions.length,
                                                " versions across base and amendments. The current version is highlighted."
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(AmendmentTimeline, {
                                            entries: timelineEntries,
                                            title: "Version history"
                                        })
                                    ]
                                }),
                                clin.notes && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Notes"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-gray-700 whitespace-pre-wrap",
                                            children: clin.notes
                                        })
                                    ]
                                }),
                                clin.tags && clin.tags.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Tags"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "flex flex-wrap gap-1",
                                            children: clin.tags.map((tag, i)=>/*#__PURE__*/ _jsx(Badge, {
                                                    variant: "secondary",
                                                    className: "text-xs",
                                                    children: tag
                                                }, i))
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "border-t pt-4",
                                    children: /*#__PURE__*/ _jsx(EntityAuditTrail, {
                                        projectId: projectId,
                                        resourceType: "clin",
                                        resourceId: clin.clinNumber
                                    })
                                })
                            ]
                        })
                    })
                ]
            }),
            viewerTarget && /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                isOpen: isDocViewerOpen,
                onClose: ()=>{
                    setIsDocViewerOpen(false);
                    setViewerTarget(null);
                },
                projectId: projectId,
                document: viewerTarget.document,
                highlightBlockId: viewerTarget.highlightBlockId,
                highlightBlockIds: viewerTarget.highlightBlockIds,
                initialPage: viewerTarget.initialPage,
                mode: "extension"
            })
        ]
    }), portalContainer);
});
// PR-C.5 helpers — render CLIN version rows for the AmendmentTimeline.
// Each timeline row has a single primary line and an optional secondary
// line. We pick the fields that actually drift across amendments
// (description, pricing, funding status, period of performance) so the
// timeline is scannable rather than showing repeated boilerplate.
function buildClinPrimary(row) {
    const title = (row.title ?? '').trim();
    const description = (row.description ?? '').trim();
    // Prefer the title; fall back to a truncated description; final fall
    // back to the CLIN number itself.
    if (title) return title;
    if (description) {
        return description.length > 60 ? `${description.slice(0, 57)}…` : description;
    }
    return row.clinNumber || 'Untitled CLIN';
}
function buildClinSecondary(row) {
    // Surface the fields most likely to drift across amendments. Each
    // included field is rendered as a small inline metadata chip; the
    // overall string stays under ~80 characters so the timeline row
    // doesn't wrap.
    const parts = [];
    if (row.pricing?.unitPrice) {
        parts.push(`unit ${row.pricing.unitPrice}`);
    }
    if (row.pricing?.extendedPrice) {
        parts.push(`total ${row.pricing.extendedPrice}`);
    }
    if (row.pricing?.ceiling) {
        parts.push(`ceiling ${row.pricing.ceiling}`);
    }
    if (row.status && row.status !== 'extracted') {
        parts.push(row.status);
    }
    if (row.optionType && row.optionType !== 'base') {
        parts.push(row.optionType.replace(/_/g, ' '));
    }
    return parts.length > 0 ? parts.join(' · ') : undefined;
}
